// tenant/InitialPickupPage.tsx
import React, { CSSProperties, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import TenantSignupLayout from '../TenantSignupLayout';

// Base signup state coming from earlier steps
type BaseSignupState = {
  facilityID: string;
  name: string;
  email: string;
  password: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  specialInstructions: string;
  size: string;
  parking: string;
  building: string;
  // licenseNumber: string;
  // licenseState: string;
  minStay: string;
  typeOfPlan: string;
  price: string;
  referralCode?: string;
};

type Address = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  specialInstructions: string;
  parking: string;
  building: string;
};

type LocationState = BaseSignupState & {
  dateSelected: string;
  timeSelected: string;
  address: Address;
  needHelp?: string;
  needPack?: string;
  selected?: string[];
  slotPaid?: boolean;
  paidPricingLabel?: string;
  tier?: string;
};

type ItemCounts = Record<string, number>;

// ---- shared styles ----
const baseFont: CSSProperties = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: '#222',
  fontSize: 14,
};

const labelStyle: CSSProperties = {
  ...baseFont,
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
  color: "#FFF"
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #CCCCCC',
  fontSize: 14,
  fontFamily: baseFont.fontFamily,
  boxSizing: 'border-box' as const,
};

const hintTextStyle: CSSProperties = {
  ...baseFont,
  fontSize: 12,
  color: '#777',
  marginTop: 4,
};

const errorTextStyle: CSSProperties = {
  ...baseFont,
  fontSize: 12,
  color: '#d32f2f',
  marginTop: 8,
};

const sectionCard: CSSProperties = {
  backgroundColor: '#3A1F52',
  borderRadius: 12,
  padding: 16,
  color: '#fff',
  marginBottom: 20,
};

const itemRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 12,
};

const numberInputStyle: CSSProperties = {
  ...inputStyle,
  width: 90,
  textAlign: 'center' as const,
};

const radioGroupStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  marginTop: 6,
};

const radioLabelStyle: CSSProperties = {
  ...baseFont,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  color: '#fff',
};

const primaryButton: CSSProperties = {
  ...baseFont,
  marginTop: 24,
  width: '100%',
  padding: '10px 16px',
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#7B1FA2',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'center' as const,
};

const InitialPickupPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const signupState = location.state;

  const [itemCounts, setItemCounts] = useState<ItemCounts>({});
  const [needPack, setNeedPack] = useState<string>('NO');
  const [needHelp, setNeedHelp] = useState<string>('NO');
  const [tier, setTier] = useState<string>('Basic');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('[InitialPickupPage] route state:', signupState);
  }, [signupState]);

  // If user hits URL directly
  if (!signupState) {
    return (
      <TenantSignupLayout
        title="Estimate your items"
        subtitle="We need your signup details before we can estimate your pickup."
        step={6}
        totalSteps={7}
      >
        <div style={{ ...baseFont }}>
          <p style={{ marginBottom: 16 }}>
            It looks like you opened this page directly. Please go back to the beginning of signup.
          </p>
          <button
            type="button"
            onClick={() => history.push('/signup')}
            style={primaryButton}
          >
            Go to signup
          </button>
        </div>
      </TenantSignupLayout>
    );
  }

  const items = [
    'Boxes',
    'Sofas / Couches',
    'Beds / Mattresses / Dressers',
    'Dining Tables / Chairs',
    'Desks and Office Furniture',
    'Large Kitchen Appliances',
    'Large Sporting Goods',
    'Large Outdoor / Patio Furniture',
    'Large Tools / Equipment',
    'Large Mirrors',
    'Large Electronics',
  ];

  const handleItemChange = (id: string, value: string) => {
    const num = Math.max(0, Number(value || 0));
    setItemCounts(prev => ({
      ...prev,
      [id]: Number.isNaN(num) ? 0 : num,
    }));
    setError(null);
  };

  const handleNext = () => {
    // Build "Selected: Label (count)" array, like mobile app did
    const selectedItems = items
      .map(label => {
        const id = label.toLowerCase();
        const count = itemCounts[id] || 0;
        if (count <= 0) return null;
        return `${label} (${count})`;
      })
      .filter(Boolean) as string[];

    if (selectedItems.length === 0) {
      // In the RN app this was allowed; here we'll be a bit more explicit
      setError('Please estimate at least one item, or enter 0 for everything if you’re not sure.');
      return;
    }

    const nextState: LocationState = {
      ...signupState,
      selected: selectedItems,
      needHelp,
      needPack,
      tier,
    };

    console.log('[InitialPickupPage] Navigating to billing info with state:', nextState);
    history.push('/signup/billing-info', nextState);
  };

  return (
    <TenantSignupLayout
      title="Estimate your items"
      step={6}
      totalSteps={7}
    >
      <div style={{ ...baseFont }}>
        {/* Item estimation card */}
        <div style={sectionCard}>
          <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 18, fontWeight: 600 }}>
            What are you planning to store?
          </h3>
          <p style={{ ...hintTextStyle, color: '#D0C9E9', marginBottom: 16 }}>
            Give us an estimate of your larger items and boxes. You can always adjust later.
          </p>

          {items.map(label => {
            const id = label.toLowerCase();
            const count = itemCounts[id] ?? 0;
            return (
              <div key={id} style={itemRowStyle}>
                <div style={{ ...baseFont, color: '#fff', maxWidth: 260 }}>{label}</div>
                <input
                  type="number"
                  min={0}
                  value={count}
                  onChange={e => handleItemChange(id, e.target.value)}
                  style={numberInputStyle}
                />
              </div>
            );
          })}

          <div style={{ ...hintTextStyle, color: '#D0C9E9', marginTop: 8 }}>
            Prohibited items: food, drugs, guns, chemicals, pianos, building materials, car parts.
            Please ensure dressers, fridges, etc., are empty before pickup.
          </div>
        </div>

        {/* Storage tier */}
        <div style={sectionCard}>
          <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
            How should we handle your items?
          </h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
            {([
              {
                key: 'Basic',
                title: 'Basic',
                desc: "We pick up, store, and return your belongings as one set. Quickest at pickup, but items aren't tracked individually — so deliveries bring back your whole storage rather than specific pieces. Choose this version if you plan to have all your items delivered back at the same time.",
              },
              {
                key: 'Itemized',
                title: 'Itemized',
                desc: "Every item is photographed and tagged as we pack, so your full inventory lives in the app. Request just one box or item and we'll bring back only that. Itemizing adds a little time at pickup, so the moving fee runs slightly higher — storage costs the same. Choose thise version if you want to choose specific items for delivery later, or if you want the peace of mind of an item-level inventory and photos.",
              },
            ]).map(opt => {
              const selected = tier === opt.key;
              return (
                <div
                  key={opt.key}
                  onClick={() => setTier(opt.key)}
                  style={{
                    flex: '1 1 200px',
                    background: selected ? 'rgba(123,31,162,0.30)' : 'rgba(255,255,255,0.06)',
                    border: selected ? '2px solid #C77BE0' : '2px solid rgba(255,255,255,0.15)',
                    borderRadius: 10,
                    padding: 14,
                    cursor: 'pointer',
                    color: '#fff',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>{opt.title}</div>
                  <div style={{ fontSize: 13, color: '#D0C9E9', lineHeight: 1.5 }}>{opt.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Help/packing options */}
        <div style={sectionCard}>
          <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
            Any extra help needed?
          </h3>

          <div style={{ marginBottom: signupState.slotPaid ? 16 : 0 }}>
            <div style={labelStyle}>Carrying / moving help</div>
            <div style={radioGroupStyle}>
              <label style={radioLabelStyle}>
                <input
                  type="radio"
                  name="needHelp"
                  value="NO"
                  checked={needHelp === 'NO'}
                  onChange={() => setNeedHelp('NO')}
                />
                I&apos;m okay loading items myself
              </label>
              <label style={radioLabelStyle}>
                <input
                  type="radio"
                  name="needHelp"
                  value="YES"
                  checked={needHelp === 'YES'}
                  onChange={() => setNeedHelp('YES')}
                />
                I&apos;d like help carrying items
              </label>
            </div>
          </div>

          {signupState.slotPaid && (
            <div>
              <div style={labelStyle}>Packing help</div>
              <div style={radioGroupStyle}>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="needPack"
                    value="NO"
                    checked={needPack === 'NO'}
                    onChange={() => setNeedPack('NO')}
                  />
                  I&apos;ll have everything packed
                </label>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="needPack"
                    value="YES"
                    checked={needPack === 'YES'}
                    onChange={() => setNeedPack('YES')}
                  />
                  I may need help packing some items
                </label>
              </div>
            </div>
          )}
        </div>

        {error && <div style={errorTextStyle}>{error}</div>}

        <button type="button" onClick={handleNext} style={primaryButton}>
          Next
        </button>
      </div>
    </TenantSignupLayout>
  );
};

export default InitialPickupPage;
